import React, { useState } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Table,
  TableHead,
  TableRow,
  TableCell,
  TableBody,
  makeStyles,
  Button,
  CircularProgress
} from '@material-ui/core';
import { useDataProvider } from 'react-admin';
import {
  predictiveSearchFormatter,
  predictiveSearchParser
} from '../../core/utils';

const useStyles = makeStyles({
  searchBox: {
    marginBottom: '16px'
  },
  clickableRow: {
    cursor: 'pointer',
    '&:hover': {
      backgroundColor: '#f5f5f5'
    }
  },
  dialogContent: {
    height: '600px',
    overflowY: 'auto'
  },
  dialogPaper: {
    minHeight: '650px',
    maxHeight: '80vh',
    width: '900px',
    maxWidth: '90%'
  },
  loaderContainer: {
    height: '100%',
    display: 'flex',
    maxHeight: '500px',
    justifyContent: 'center',
    alignItems: 'center'
  }
});

const SelectMainFundModal = ({ open, onClose, onSelect, fundId }) => {
  const classes = useStyles();
  const dataProvider = useDataProvider();
  const [search, setSearch] = useState('');
  const [funds, setFunds] = useState([]);
  const [loading, setLoading] = useState(false);
  const [selectedFund, setSelectedFund] = useState(null);

  React.useEffect(() => {
    const fetchFunds = async () => {
      setLoading(true);
      try {
        const { data } = await dataProvider.getList('funds', {
          pagination: { page: 1, perPage: 100 },
          sort: { field: 'name', order: 'ASC' },
          filter: search ? { name: predictiveSearchParser(search) } : {}
        });
        const filteredFunds = data.filter(fund => fund.id !== fundId);
        setFunds(filteredFunds);
      } catch (error) {
        console.error('Error fetching funds:', error);
      } finally {
        setLoading(false);
      }
    };

    if (open) {
      fetchFunds();
    }
  }, [open, search, dataProvider, fundId]);

  return (
    <Dialog
      fullWidth
      open={open}
      maxWidth="md"
      onClose={(event, reason) => {
        if (reason === 'backdropClick') return null;
        onClose();
        setSelectedFund(null);
      }}
    >
      <DialogTitle>Select Main Fund</DialogTitle>
      <DialogContent>
        <TextField
          label="Search Fund"
          variant="standard"
          fullWidth
          className={classes.searchBox}
          value={predictiveSearchFormatter(search)}
          onChange={e => setSearch(e.target.value)}
        />
        {loading ? (
          <div className={classes.loaderContainer}>
            <CircularProgress />
          </div>
        ) : (
          <Table>
            <TableHead>
              <TableRow>
                <TableCell>Fund Name</TableCell>
                <TableCell>Fund ID</TableCell>
                <TableCell>Status</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {funds.map(fund => {
                const isSelected = selectedFund?.id === fund.id;
                return (
                  <TableRow
                    key={fund.id}
                    className={classes.clickableRow}
                    onClick={() => setSelectedFund(isSelected ? null : fund)}
                    style={{
                      backgroundColor: isSelected ? '#f0f0f0' : 'white',
                      cursor: 'pointer'
                    }}
                  >
                    <TableCell>{fund.name}</TableCell>
                    <TableCell>{fund.id}</TableCell>
                    <TableCell>{fund.status}</TableCell>
                  </TableRow>
                );
              })}
            </TableBody>
          </Table>
        )}
      </DialogContent>
      <DialogActions style={{ borderTop: '1px solid rgba(0, 0, 0, 0.12)' }}>
        <Button
          onClick={() => {
            if (selectedFund) {
              onSelect(selectedFund);
            }
            setSelectedFund(null);
            onClose();
          }}
          color="primary"
          variant="contained"
          disabled={!selectedFund}
        >
          Add
        </Button>
        <Button
          onClick={() => {
            setSelectedFund(null);
            onClose();
          }}
          color="primary"
          variant="outlined"
        >
          Cancel
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default SelectMainFundModal;
